import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Divider, Tooltip } from '@mui/material';
import {
  InfoOutlined, CalendarMonthOutlined, RecordVoiceOverOutlined, StoreOutlined,
  PeopleOutlined, AssignmentOutlined, TuneOutlined, BarChartOutlined,
  SaveOutlined, CheckCircleOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { PRIMARY } from './shared';

import EventInfoTab        from './eventDetailsTab/EventInfoTab';
import EventScheduleTab    from './eventDetailsTab/EventScheduleTab';
import SpeakersTab         from './eventDetailsTab/SpeakersTab';
import ExhibitorsManageTab from './eventDetailsTab/ExhibitorsManageTab';
import SettingsTab         from './eventDetailsTab/SettingsTab';
import ParticipantsTab     from './eventDetailsTab/ParticipantsTab';
import SubmissionsTab      from './eventDetailsTab/SubmissionsTab';

const SIDEBAR_W = 200;

const SIDE_GROUPS = [
  {
    label: 'Event Details',
    items: [
      { id: 'info',         label: 'Event Information', icon: <InfoOutlined sx={{ fontSize: 15 }} /> },
      { id: 'schedule',     label: 'Event Schedule',    icon: <CalendarMonthOutlined sx={{ fontSize: 15 }} /> },
      { id: 'speakers',     label: 'Speakers',          icon: <RecordVoiceOverOutlined sx={{ fontSize: 15 }} /> },
      // { id: 'exhibitors',   label: 'Exhibitors',        icon: <StoreOutlined sx={{ fontSize: 15 }} /> },
      // { id: 'settings',     label: 'Settings',          icon: <TuneOutlined sx={{ fontSize: 15 }} /> },
    ],
  },
  {
    label: 'Event Info',
    items: [
      { id: 'participants', label: 'Participants', icon: <PeopleOutlined sx={{ fontSize: 15 }} /> },
      { id: 'submissions',  label: 'Submissions',  icon: <AssignmentOutlined sx={{ fontSize: 15 }} /> },
      { id: 'analytics',    label: 'Analytics',    icon: <BarChartOutlined sx={{ fontSize: 15 }} /> },
    ],
  },
];

const VALID_SUBTABS = SIDE_GROUPS.flatMap(g => g.items.map(i => i.id));
const DEFAULT_SUBTAB = 'info';

function Placeholder({ label }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 1.5, opacity: 0.3 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="body2" color="text.secondary">Coming soon</Typography>
    </Box>
  );
}

// Spinner via CSS animation — no extra deps
const Spinner = () => (
  <Box component="span" sx={{
    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
    border: '1.5px solid currentColor', borderTopColor: 'transparent',
    animation: 'edt-spin 0.7s linear infinite',
    '@keyframes edt-spin': { to: { transform: 'rotate(360deg)' } },
  }} />
);

export default function EventDetailsTab({ event, onSave }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Subtab lives in ?t=<subtab> — path never changes so EventEditor stays fully mounted
  const subtab    = searchParams.get('t');
  const activeTab = VALID_SUBTABS.includes(subtab) ? subtab : DEFAULT_SUBTAB;

  // Track which subtabs have been visited — a tab only mounts on first activation,
  // then stays mounted forever so switching never triggers a reload.
  const [activated, setActivated] = useState(() => ({ [activeTab]: true }));
  useEffect(() => {
    if (activeTab && !activated[activeTab]) {
      setActivated(prev => ({ ...prev, [activeTab]: true }));
    }
  }, [activeTab]); // eslint-disable-line

  // ── Save plumbing ────────────────────────────────────────────────────────────
  // One ref per saveable tab. Tabs that don't expose save() will have ref.current = null.
  const tabRefs = {
    info:       useRef(null),
    schedule:   useRef(null),
    speakers:   useRef(null),
    exhibitors: useRef(null),
    settings:   useRef(null),
  };

  // Mirror saving/saved from the active tab's ref into state (poll at 80ms).
  // This is necessary because ref.current values don't trigger re-renders.
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [toast, setToast]           = useState(null);   // null | 'saving' | 'saved'
  const toastTimer = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      const ref = tabRefs[activeTab]?.current;
      if (!ref) return;
      const next = ref.saving ? 'saving' : ref.saved ? 'saved' : 'idle';
      setSaveStatus(prev => {
        if (prev === next) return prev;
        if (next === 'saving') {
          clearTimeout(toastTimer.current);
          setToast('saving');
        }
        if (next === 'saved') {
          clearTimeout(toastTimer.current);
          setToast('saved');
          toastTimer.current = setTimeout(() => setToast(null), 2500);
        }
        if (next === 'idle' && prev === 'saving') {
          // ended without saved (error) — clear toast quickly
          clearTimeout(toastTimer.current);
          toastTimer.current = setTimeout(() => setToast(null), 800);
        }
        return next;
      });
    }, 80);
    return () => clearInterval(interval);
  }, [activeTab]); // eslint-disable-line

  // Reset on tab switch
  useEffect(() => {
    setSaveStatus('idle');
    clearTimeout(toastTimer.current);
    setToast(null);
  }, [activeTab]);

  // Only show the toolbar save button when the active tab exposes a real save()
  const canSave = typeof tabRefs[activeTab]?.current?.save === 'function';
  const handleToolbarSave = useCallback(() => {
    tabRefs[activeTab]?.current?.save?.();
  }, [activeTab]); // eslint-disable-line

  const goToSubtab = (id) => {
    setSearchParams({ t: id }, { replace: true });
  };

  // ── Shared toolbar pill ──────────────────────────────────────────────────────
  const SaveButton = (
    <Tooltip title={saveStatus === 'saved' ? 'Saved!' : saveStatus === 'saving' ? 'Saving…' : 'Save'}>
      <Box
        onClick={saveStatus === 'saving' ? undefined : handleToolbarSave}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          px: 1.5, height: 30, borderRadius: 1.5,
          fontSize: '0.75rem', fontWeight: 600,
          transition: 'background 0.2s, color 0.2s, border-color 0.2s',
          cursor: saveStatus === 'saving' ? 'default' : 'pointer',
          userSelect: 'none',
          ...(saveStatus === 'saved'
            ? { background: alpha('#22c55e', 0.1), color: '#16a34a', border: '1px solid #86efac' }
            : saveStatus === 'saving'
            ? { background: alpha('#f59e0b', 0.08), color: '#b45309', border: '1px solid #fcd34d', opacity: 0.85 }
            : { background: '#f0f4f8', color: '#5a6474', border: '1px solid #e4e9f0', '&:hover': { background: alpha(PRIMARY, 0.06), color: PRIMARY, borderColor: alpha(PRIMARY, 0.3) } }
          ),
        }}
      >
        {saveStatus === 'saved'
          ? <CheckCircleOutlined sx={{ fontSize: 14 }} />
          : saveStatus === 'saving'
          ? <Spinner />
          : <SaveOutlined sx={{ fontSize: 14 }} />}
        <Box component="span">
          {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : 'Save'}
        </Box>
      </Box>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', background: '#fff', width: '100%', overflow: 'hidden', height: 'calc(100vh - 60px)' }}>

      {/* Centered container */}
      <Box sx={{ width: '100%', mx: 'auto', display: 'flex', height: '100%', minHeight: 0 }}>

        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <Box sx={{
          width: SIDEBAR_W,
          flexShrink: 0,
          borderRight: '1px solid #e0e5ec',
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto',
          background: '#fff',
        }}>
          {SIDE_GROUPS.map((group, gi) => (
            <Box key={group.label}>
              {gi > 0 && <Divider sx={{ my: 2, borderColor: '#f0f2f5' }} />}
              <Typography sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#9ea8b3',
                px: 2.5, mb: 0.5,
              }}>
                {group.label}
              </Typography>
              {group.items.map(item => {
                const active = activeTab === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => goToSubtab(item.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25,
                      px: 2.5, py: 0.9,
                      cursor: 'pointer',
                      background: active ? alpha(PRIMARY, 0.07) : 'transparent',
                      color: active ? PRIMARY : '#374151',
                      fontWeight: active ? 600 : 400,
                      fontSize: '0.875rem',
                      borderLeft: active ? `3px solid ${PRIMARY}` : '3px solid transparent',
                      transition: 'all 0.12s ease',
                      '&:hover': { background: alpha(PRIMARY, 0.04), color: active ? PRIMARY : '#1a1a2e' },
                      userSelect: 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', color: 'inherit', flexShrink: 0 }}>{item.icon}</Box>
                    {item.label}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* ── Right panel ───────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

          {/* ── Toolbar — always rendered, save button only when tab supports it ── */}
          <Box sx={{ background: '#fff', borderBottom: '1px solid #e8edf2', px: 3, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0 }}>
            {canSave && SaveButton}
          </Box>

          {/* Schedule — its own flex/scroll container */}
          <Box sx={{ display: activeTab === 'schedule' ? 'flex' : 'none', flex: 1, minHeight: 0, overflow: 'hidden', px: 4, pt: 3, pb: 0, flexDirection: 'column' }}>
            {activated.schedule && <EventScheduleTab ref={tabRefs.schedule} event={event} onSave={onSave} />}
          </Box>

          {/* All other subtabs — shared scroll container */}
          <Box sx={{ display: activeTab === 'schedule' ? 'none' : 'block', px: 4, py: 4, minWidth: 0, flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <Box sx={{ display: activeTab === 'info'         ? 'block' : 'none' }}>{activated.info         && <EventInfoTab        ref={tabRefs.info}       event={event} onSave={onSave} />}</Box>
            <Box sx={{ display: activeTab === 'speakers'     ? 'block' : 'none' }}>{activated.speakers     && <SpeakersTab         ref={tabRefs.speakers}   event={event} onSave={onSave} />}</Box>
            <Box sx={{ display: activeTab === 'exhibitors'   ? 'block' : 'none' }}>{activated.exhibitors   && <ExhibitorsManageTab ref={tabRefs.exhibitors} event={event} isActive={activeTab === 'exhibitors'} />}</Box>
            <Box sx={{ display: activeTab === 'participants' ? 'block' : 'none' }}>{activated.participants && <ParticipantsTab     event={event} isActive={activeTab === 'participants'} />}</Box>
            <Box sx={{ display: activeTab === 'submissions'  ? 'block' : 'none' }}>{activated.submissions  && <SubmissionsTab      event={event} isActive={activeTab === 'submissions'} />}</Box>
            <Box sx={{ display: activeTab === 'analytics'    ? 'block' : 'none' }}>{activated.analytics    && <Placeholder label="Analytics" />}</Box>
            {/* <Box sx={{ display: activeTab === 'settings' ? 'block' : 'none' }}>{activated.settings && <SettingsTab ref={tabRefs.settings} event={event} onSave={onSave} />}</Box> */}
          </Box>
        </Box>
      </Box>

      {/* ── Toast (lower-right, fixed) ─────────────────────────────────────── */}
      {toast && (
        <Box sx={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 1400,
          display: 'flex', alignItems: 'center', gap: 1,
          px: 2, py: 1.25, borderRadius: 2,
          fontSize: '0.8rem', fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          background: '#fff',
          ...(toast === 'saved'
            ? { border: '1px solid #86efac', color: '#16a34a' }
            : { border: '1px solid #fcd34d', color: '#b45309' }
          ),
        }}>
          {toast === 'saved'
            ? <CheckCircleOutlined sx={{ fontSize: 16 }} />
            : <Box component="span" sx={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', animation: 'edt-spin 0.7s linear infinite', '@keyframes edt-spin': { to: { transform: 'rotate(360deg)' } }, flexShrink: 0 }} />}
          <Box component="span">{toast === 'saved' ? 'Changes saved' : 'Saving…'}</Box>
        </Box>
      )}
    </Box>
  );
}